import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  MessageSquare,
  Wand2,
  FileSearch,
  BookOpen,
  Lightbulb,
  X,
} from 'lucide-react';

interface AIFloatingButtonProps {
  onOpenChat: () => void;
  onOpenGenerator: () => void;
  onOpenAnalyzer: () => void;
  onOpenSmartFill: () => void;
  onOpenClauseSuggestions: () => void;
  showAnalyzer?: boolean;
  showSmartFill?: boolean;
  showClauseSuggestions?: boolean;
}

export function AIFloatingButton({
  onOpenChat,
  onOpenGenerator,
  onOpenAnalyzer,
  onOpenSmartFill,
  onOpenClauseSuggestions,
  showAnalyzer = false,
  showSmartFill = false,
  showClauseSuggestions = false,
}: AIFloatingButtonProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const actions = [
    {
      id: 'chat',
      label: 'AI Assistant',
      description: 'Chat with Claude',
      icon: MessageSquare,
      color: 'from-amber-500 to-orange-600',
      onClick: onOpenChat,
      show: true,
    },
    {
      id: 'generator',
      label: 'Generate Template',
      description: 'Create from description',
      icon: Wand2,
      color: 'from-violet-500 to-purple-600',
      onClick: onOpenGenerator,
      show: true,
    },
    {
      id: 'analyzer',
      label: 'Analyze Document',
      description: 'Review for risks',
      icon: FileSearch,
      color: 'from-cyan-500 to-blue-600',
      onClick: onOpenAnalyzer,
      show: showAnalyzer,
    },
    {
      id: 'smartfill',
      label: 'Smart Fill',
      description: 'Extract from text',
      icon: BookOpen,
      color: 'from-emerald-500 to-teal-600',
      onClick: onOpenSmartFill,
      show: showSmartFill,
    },
    {
      id: 'clauses',
      label: 'Suggest Clauses',
      description: 'Get recommendations',
      icon: Lightbulb,
      color: 'from-pink-500 to-rose-600',
      onClick: onOpenClauseSuggestions,
      show: showClauseSuggestions,
    },
  ].filter(action => action.show);

  return (
    <div ref={menuRef} className="fixed bottom-6 right-6 z-40">
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="absolute bottom-16 right-0 bg-slate-900 border border-slate-700 rounded-2xl p-2 shadow-2xl min-w-[220px]"
          >
            <div className="p-2 border-b border-slate-700 mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium text-white">AI Features</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Powered by Claude</p>
            </div>
            
            <div className="space-y-1">
              {actions.map((action, index) => (
                <motion.button
                  key={action.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => {
                    action.onClick();
                    setIsExpanded(false);
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-800 transition-colors text-left group"
                >
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${action.color} flex items-center justify-center flex-shrink-0`}>
                    <action.icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white group-hover:text-amber-400 transition-colors">
                      {action.label}
                    </p>
                    <p className="text-xs text-slate-500">{action.description}</p>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
          isExpanded
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500'
        }`}
      >
        <AnimatePresence mode="wait">
          {isExpanded ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
            >
              <X className="w-6 h-6 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="sparkles"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
            >
              <Sparkles className="w-6 h-6 text-white" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Tooltip when collapsed */}
      {!isExpanded && (
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg whitespace-nowrap pointer-events-none"
        >
          <span className="text-sm text-white">AI Features</span>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-2 h-2 bg-slate-800 border-r border-b border-slate-700 rotate-[-45deg]" />
        </motion.div>
      )}
    </div>
  );
}

export default AIFloatingButton;
